import type * as Allergy from '../allergy/index.js';
import {
  type BaseItem,
  type NullablePartial,
  type ProviderNameDisplayOption,
  type Relationship,
  type Sex,
  TimingUnit,
} from '../common.js';
import { type DigitalCheckInAppointmentStatus } from '../digitalCheckIn.js';
import { type AppointmentKind, type EpicAppMarketSchedulingAPI } from '../ehrTimeSlot.js';
import { type Employer } from '../employer.js';
import { type Timeframe } from '../encounter.js';
import { type FamilyRelationship } from '../familyMedicalHistory.js';
import { type EhrProviderInformation, type Previsit } from '../index.js';
import type * as Medication from '../medication.js';
import type * as PatientEnvironment from '../patient/environment.js';
import type * as Guarantor from '../patient/guarantor.js';
import { type HealthPlansToExport } from '../patient/insuranceInformation.js';
import { type Kind as NotificationModality } from '../patient/notification.js';
import type * as Procedure from '../patient/procedure.js';
import type * as PatientSocialHistory from '../patient/socialHistory.js';
import type * as Patient from '../patient.js';
import type * as PatientAdvisory from '../patientAdvisory.js';
import { type DocumentType } from '../patientUpload.js';
import type * as Payment from '../payment/payment.js';
import type * as PracticeEntity from '../practiceEntity.js';
import { Primitive } from '../previsit/formTemplateFieldConfig.js';
import { type Language } from '../previsit/language.js';
import { type MaritalStatus } from '../previsit.js';
import type * as SubstanceUsage from '../substanceUsage.js';
import type * as Workflow from '../workflow.js';

export const LOGIN_FIELDS = [
  Primitive.PatientEmail,
  Primitive.PatientCellPhone,
  Primitive.PatientSex,
  Primitive.PatientAddressPhysical,
] as const;

export type PatientLoginInput = (typeof LOGIN_FIELDS)[number];

export const PREVISIT_LOGIN_FIELDS = [Primitive.PatientAddressMailing] as const;

export type PrevisitLoginInput = (typeof PREVISIT_LOGIN_FIELDS)[number];

export const GROUPED_APPOINTMENT_PRIMITIVES = [
  Primitive.PatientGuarantorVerification,
  Primitive.PatientInsurance,
] as const;

export type PrimitivesToSyncForGroupedAppointments =
  (typeof GROUPED_APPOINTMENT_PRIMITIVES)[number];

export enum PrevisitAddressValidCharacterSet {
  Any = 'ANY',
  AlphanumericAndSlash = 'ALPHANUMERIC_AND_SLASH',
}

enum PrevisitSatisfactionFeedbackCondition {
  Never = 'NEVER',
  Monthly = 'MONTHLY',
  Yearly = 'YEARLY',
}

export enum EmploymentStatus {
  Disabled = 'DISABLED',
  Employed = 'EMPLOYED',
  NotEmployed = 'NOT_EMPLOYED',
  FullTime = 'FULL_TIME',
  OnActiveMilitaryDuty = 'ON_ACTIVE_MILITARY_DUTY',
  Other = 'OTHER',
  PartTime = 'PART_TIME',
  Retired = 'RETIRED',
  RetiredMilitary = 'RETIRED_MILITARY',
  SelfEmployed = 'SELF_EMPLOYED',
  StudentFullTime = 'STUDENT_FULL_TIME',
  StudentPartTime = 'STUDENT_PART_TIME',
  Unknown = 'UNKNOWN',
  Minor = 'MINOR',
}

export enum Frequency {
  DailySeveralTimes = 'DAILY_SEVERAL_TIMES',
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  WeeklyMoreThanThree = 'WEEKLY_MORE_THAN_THREE',
  Monthly = 'MONTHLY',
  Yearly = 'YEARLY',
  Other = 'OTHER',
  OncePerWeek = 'ONCE_PER_WEEK',
  TwoTimesPerWeek = 'TWO_TIMES_PER_WEEK',
  ThreeTimesPerWeek = 'THREE_TIMES_PER_WEEK',
  FourTimesPerWeek = 'FOUR_TIMES_PER_WEEK',
  FiveTimesPerWeek = 'FIVE_TIMES_PER_WEEK',
  SixTimesPerWeek = 'SIX_TIMES_PER_WEEK',
  EveryDayOfTheWeek = 'EVERY_DAY_OF_THE_WEEK',
}

export enum AutoCopayConflictResolutionMode {
  Default = 'DEFAULT',
  LeastAmount = 'LEAST_AMOUNT',
}

enum BrandingScheme {
  Notable = 'NOTABLE',
  Altais = 'ALTAIS',
}

enum PhoneNumberKind {
  Cell = 'CELL',
  Work = 'WORK',
  Home = 'HOME',
}

enum ExtractOutstandingBalancesEnabled {
  Enabled = 'ENABLED',
  Disabled = 'DISABLED',
  EnabledGuarantorIdRequired = 'ENABLED_GUARANTOR_ID_REQUIRED',
}

enum ExtractPatientEmploymentSource {
  ExtractPatientInformation = 'EXTRACT_PATIENT_INFORMATION',
  ExtractSocialHistory = 'EXTRACT_SOCIAL_HISTORY',
}

enum AppointmentNoteStatus {
  UndeliverablePhone = 'UNDELIVERABLE_PHONE',
  Incomplete = 'INCOMPLETE',
  Complete = 'COMPLETE',
  ReviewRequired = 'REVIEW_REQUIRED',
}

export enum PreferenceType {
  EmailTemplate = 'EMAIL_TEMPLATE',
  InsuranceProviderIds = 'INSURANCE_PROVIDER_IDS',
  LocalizedText = 'LOCALIZED_TEXT',
  KeyedNotificationContent = 'KEYED_NOTIFICATION_CONTENT',
  KeyedText = 'KEYED_TEXT',
  KeyValueMappingSelect = 'KEY_VALUE_MAPPING_SELECT',
  MultiSelect = 'MULTI_SELECT',
  MultiSelectMultiOptions = 'MULTI_SELECT_MULTI_OPTIONS',
  NotificationContentEmailId = 'NOTIFICATION_CONTENT_EMAIL_ID',
  NotificationContentSmsId = 'NOTIFICATION_CONTENT_SMS_ID',
  NotificationSchemeId = 'NOTIFICATION_SCHEME_ID',
  NotificationContentKindIds = 'NOTIFICATION_CONTENT_KIND_IDS',
  Object = 'OBJECT',
  PracticeId = 'PRACTICE_ID',
  SingleSelect = 'SINGLE_SELECT',
  SingleSelectEditable = 'SINGLE_SELECT_EDITABLE',
  Switch = 'SWITCH',
  TimeWithUnitPicker = 'TIME_WITH_UNIT_PICKER',
  DateWithUnitPicker = 'DATE_WITH_UNIT_PICKER',
  BidirectionalTimeWithUnitPicker = 'BIDIRECTIONAL_TIME_WITH_UNIT_PICKER',
  AutoCompleteMultiSelect = 'AUTO_COMPLETE_MULTI_SELECT',
  AsyncAutoCompleteMultiSelect = 'ASYNC_AUTO_COMPLETE_MULTI_SELECT',
  MultiText = 'MULTI_TEXT',
}

export enum ClinicalCommentSection {
  AllergiesAdded = 'ALLERGIES_ADDED',
  ExerciseRoutineChanged = 'EXERCISE_ROUTINE_CHANGED',
  FamilyMedicalHistoryAdded = 'FAMILY_MEDICAL_HISTORY_ADDED',
  FormsCompleted = 'FORMS_COMPLETED',
  MedicationStatusUpdated = 'MEDICATION_STATUS_UPDATED',
  PastMedicalHistoryAdded = 'PAST_MEDICAL_HISTORY_ADDED',
  PatientCareProviderChanged = 'PATIENT_CARE_PROVIDER_CHANGED',
  PatientEnvironmentChanged = 'PATIENT_ENVIRONMENT_CHANGED',
  PediatricEnvironmentChanged = 'PEDIATRIC_ENVIRONMENT_CHANGED',
  PharmaciesChanged = 'PHARMACIES_CHANGED',
  ResidualCareProviders = 'RESIDUAL_CARE_PROVIDERS',
  ResidualPharmacies = 'RESIDUAL_PHARMACIES',
  SexualHealthChanged = 'SEXUAL_HEALTH_CHANGED',
  SexualIdentityChanged = 'SEXUAL_IDENTITY_CHANGED',
  SubstanceAbuseHistoryChanged = 'SUBSTANCE_ABUSE_HISTORY_CHANGED',
  SurgicalHistoryAdded = 'SURGICAL_HISTORY_ADDED',
  TobaccoHistoryChanged = 'TOBACCO_HISTORY_CHANGED',
}

export enum GeneralCommentSection {
  LeftToDo = 'LEFT_TO_DO',
  ForStaffReview = 'FOR_STAFF_REVIEW',
  HasBeenUpdated = 'HAS_BEEN_UPDATED',
  DocumentsUploaded = 'DOCUMENTS_UPLOADED',
  PaymentsCollected = 'PAYMENTS_COLLECTED',
  DocumentsNotSignedByPatient = 'DOCUMENTS_NOT_SIGNED_BY_PATIENT',
  SectionsCompletedByPatient = 'SECTIONS_COMPLETED_BY_PATIENT',
  AppointmentInfo = 'APPOINTMENT_INFO',
  PatientInstructionsReviewed = 'PATIENT_INSTRUCTIONS_REVIEWED',
}

export enum LeftToDoField {
  InsuranceCard = 'INSURANCE_CARD',
  DriversLicense = 'DRIVERS_LICENSE',
  Copay = 'COPAY',
  Documents = 'DOCUMENTS',
}

export enum NeedsToBeUpdatedField {
  PatientInformation = 'PATIENT_INFORMATION',
  EmergencyContacts = 'EMERGENCY_CONTACTS',
  PatientUploads = 'PATIENT_UPLOADS',
  PatientCareProvider = 'PATIENT_CARE_PROVIDER',
  PatientEmployment = 'PATIENT_EMPLOYMENT',
  PhotoID = 'PHOTO_ID',
  MissingGuarantor = 'MISSING_GUARANTOR',
  TerminatedInsurance = 'TERMINATED_INSURANCES',
  InsuranceInformation = 'INSURANCE_INFORMATION',
  UnverifiedInsuranceInformation = 'UNVERIFIED_INSURANCE_INFORMATION',
}

enum HasBeenUpdatedField {
  MissingGuarantor = 'MISSING_GUARANTOR',
  PatientInformation = 'PATIENT_INFORMATION',
}

export enum FormScoreBasedMessageSettings {
  PrevisitFormTemplateId = 'PREVISIT_FORM_TEMPLATE_ID',
  MinimumFormScore = 'MINIMUM_FORM_SCORE',
  MaximumFormScore = 'MAXIMUM_FORM_SCORE',
  MessageContent = 'MESSAGE_CONTENT',
}

export enum RegistrationEncounterEhrReferenceCreationType {
  Generate = 'GENERATE',
}

export enum CopayPrepayDisplaySetting {
  CopayAndPrepay = 'COPAY_AND_PREPAY',
  CopayIfNoPrepay = 'COPAY_IF_NO_PREPAY',
  CopayOnly = 'COPAY_ONLY',
  PrepayOverCopay = 'PREPAY_OVER_COPAY',
}

export enum PreviousBalanceCollectionSetting {
  Off = 'OFF',
  FullOnly = 'FULL_ONLY',
  FullAndPartial = 'FULL_AND_PARTIAL',
}

export enum VerifyRegistrationSections {
  Patient = 'PATIENT',
  Guarantor = 'GUARANTOR',
  HospitalAccount = 'HOSPITAL_ACCOUNT',
}

export type GeneralCommentField = LeftToDoField | NeedsToBeUpdatedField | HasBeenUpdatedField;

export interface MultiOptionSection<T> {
  section: string;
  fields: T[];
}

export interface MultiOptionSections<T> {
  sections: MultiOptionSection<T>[];
}

export interface TimeLookahead {
  time: number;
  unit: TimingUnit;
}

export enum RteInsuranceCoverageMatchingPrimaryIdentifier {
  MRN = 'mrn',
  FIN = 'fin',
}

export const TimeUnitToMomentUnit: Record<
  TimingUnit,
  'days' | 'hours' | 'minutes' | 'months' | 'years' | 'weeks'
> = {
  [TimingUnit.BusinessDay]: 'days',
  [TimingUnit.Day]: 'days',
  [TimingUnit.Hour]: 'hours',
  [TimingUnit.Minute]: 'minutes',
  [TimingUnit.Month]: 'months',
  [TimingUnit.Year]: 'years',
  [TimingUnit.Week]: 'weeks',
} as const;

interface DynamicPatientProblemsOptionsConfig {
  [specialty: string]: string[];
}

export interface RelationshipPreference {
  isReverseRelationship: boolean;
  relationships: Relationship[];
}

export type LocalizedPreference = {
  [language in Language]?: string;
};

export interface Email {
  subject: string;
  html: string;
}

export type LocalizedEmail = {
  [language in Language]?: Email;
};

export type NotificationContentIds = Partial<Record<NotificationModality, string | null>>;

export enum NameLocation {
  AllStartTimes = 'ALL_START_TIMES',
}

export enum FhirReconciliationType {
  Disabled = 'DISABLED',
  ExistingOnly = 'EXISTING_ONLY',
  NewAndExisting = 'NEW_AND_EXISTING',
  ExistingOnlyWithEhrRecord = 'EXISTING_ONLY_WITH_EHR_RECORD',
}

export enum DigitalCheckInEligibilityOptions {
  InsuranceVerifySourceEligibility = 'INSURANCE_VERIFY_SOURCE_ELIGIBILITY',
  NoGuarantorChanges = 'NO_GUARANTOR_CHANGES',
  NoInsuranceChanges = 'NO_INSURANCE_CHANGES',
  NoOutstandingCoPayPayments = 'NO_OUTSTANDING_CO_PAY_PAYMENTS',
  NoOutstandingPrePayPayments = 'NO_OUTSTANDING_PRE_PAY_PAYMENTS',
  NoPhotoId = 'NO_PHOTO_ID',
  NoUnsignedDocuments = 'NO_UNSIGNED_DOCUMENTS',
  SameDayAppointmentsEligibility = 'SAME_DAY_APPOINTMENTS_ELIGIBILITY',
}

export enum ProvisionalPatientLastNameFormattingMode {
  RemoveAllHyphens = 'REMOVE_ALL_HYPHENS',
  RemoveSuffixesHyphens = 'REMOVE_SUFFIXES_HYPHENS',
}

export enum PracticeCardOption {
  NoAddress = 'NO_ADDRESS',
  NoPhone = 'NO_PHONE',
}

export interface Preferences {
  alcoholStatuses: PatientSocialHistory.AlcoholUsageStatus[];
  allergyExplicitNoneEnabled: boolean;
  allergyNames: Allergy.AllergyName[];
  allergyReactions: Allergy.Reaction[];
  allergyPrevisitRemovesEnabled: boolean;
  allergySeverityOptions: Allergy.Severity[];
  allergyStatusOnExport: string;
  allowSelfForGuardianRelationship: boolean;
  allowStoringCardExpirationDate: boolean | null;
  allowedDomains: string[];
  allowedAppointmentNotificationModalities: NotificationModality[];
  appointmentCancellationEmail: string | null;
  appointmentConfirmationDelayMinutes: number | null;
  appointmentConfirmationEmail: string | null;
  appointmentConfirmationSms: string | null;
  appointmentGroupingKey: string | null;
  appointmentNoteStatusTexts: Partial<Record<AppointmentNoteStatus, string>>;
  autoCopayConflictResolutionMode: AutoCopayConflictResolutionMode | null;
  autoCopayIngestEnabled: boolean | null;
  bookedAppointmentsProviderInformationKind: EhrProviderInformation.Kind;
  brandingScheme: BrandingScheme;
  cancellationAppointmentKindsExcluded: AppointmentKind[];
  clinicalCommentSections: ClinicalCommentSection[];
  collectEmployerPhoneNumber: boolean;
  contactVerificationEmail: string | null;
  contraceptiveOptions: PatientSocialHistory.Contraceptive[];
  copayPrepayDisplaySetting: CopayPrepayDisplaySetting;
  createEncounterOnBookedSlotEnabled: boolean | null;
  daysToStoreVisitRecordingsAndTranscripts: number;
  defaultNotificationScheme: string | null;
  digitalArrivalCustomContent: LocalizedPreference;
  digitalArrivalEligibility: DigitalCheckInEligibilityOptions[];
  digitalArrivalEligibleEncounterPayorCheck: string[];
  digitalArrivalIneligibleInsurancePlanCheck: string[];
  digitalCheckInEncounterEligibleNotificationContentIds: NotificationContentIds | null;
  digitalCheckInEncounterIneligibleNotificationContentIds: NotificationContentIds | null;
  digitalCheckInIncompleteFormsetNotificationContentIds: NotificationContentIds | null;
  digitalArrivalIdentificationConfirmationRequired: boolean | null;
  digitalCheckInUnsupportedVisitReasons: string[];
  digitalArrivalMinimumCheckInAge: number | null;
  digitalArrivalNotificationSendTimeOffset: number | null;
  digitalCheckInAppointmentStatusEhrValue: DigitalCheckInAppointmentStatus | null;
  digitalCheckInCustomArrivedContent: LocalizedPreference;
  digitalCheckInPostEncounterAuthCodeExpirationOffsetMinutes: number | null;
  digitalCheckInUseEhrReferenceValue: boolean | null;
  disabledHccSuspectingConditions: string[];
  disableSmsForNonKba: boolean;
  displayAuthCodeInAppointmentNote: boolean;
  displayAuthCodeStaffApp: boolean;
  dynamicPatientProblemsOptionsConfig: DynamicPatientProblemsOptionsConfig;
  emergencyContactRelationships: Patient.EmergencyContactRelationship[];
  employers: Employer[];
  employmentStatuses: EmploymentStatus[];
  enableAppointmentStatusConfirmedOnPrevisitLogin: boolean;
  enableCancellations: boolean;
  enableDocumentSignerV2: boolean;
  enableDocumentSignerTemplateConfigurationV2: boolean;
  enableEncounterReconciliationOnPrevisitExport: boolean;
  enableOutreachIntakes: boolean;
  enablePaymentBalancesIngestion: boolean;
  enablePaymentCaptureOnAppointmentStart: boolean;
  enablePaymentCaptureOnAuthorization: boolean;
  enablePaymentCaptureOnSetTime: string;
  enablePaymentConfirmationMessage: boolean;
  enablePaymentReceipts: boolean;
  enablePaymentReceiptEmailAttachments: boolean;
  enablePostPayoutRefunds: boolean;
  enablePrevisit: boolean;
  enablePrevisitEmailNotifications: boolean;
  enablePrevisitGuarantorForAdults: boolean;
  enablePrevisitPassBarcodes: boolean;
  enablePrevisitPassbookAndPayPass: boolean;
  enablePrevisitSatisfactionFeedback: PrevisitSatisfactionFeedbackCondition;
  enablePrevisitSubscriberInfoCollection: boolean;
  enablePrevisitSubscriberToGuarantorRelationshipCollection: boolean;
  enableProviderDirectory: boolean;
  enableRescheduling: boolean;
  encounterCreationPercentageChangeThreshold: boolean;
  epicAppMarketSchedulingAPI: EpicAppMarketSchedulingAPI;
  exportAppointmentStatusEnabled: boolean;
  exportDummyValuesEnabled: boolean;
  exportEncounterInformationEnabled: boolean;
  exportGeneralCommentCustomNote: string | null;
  exportGeneralCommentMessageIfNoUpdatesToBeMade: string | null;
  exportGuarantorsEnabled: boolean;
  exportIncludeReconciliationPatientInformation: boolean;
  exportInsuranceWithNullCoverageStartDateEnabled: boolean;
  exportModifiedGuarantorsEnabled: boolean;
  exportOnlyInsurancesWithPlan: boolean;
  exportPatientAddressEnabled: boolean;
  exportPatientEmailEnabled: boolean;
  exportPayingWithoutInsuranceEnabled: boolean;
  exportPaymentsOnAuthorization: boolean;
  exportPrevisitFormTemplateAsFormEnabled: boolean;
  exportPrevisitPatientEmployerIfEmploymentStatusUpdatedEnabled: boolean;
  exportPrevisitPatientInsuranceInformationEnabled: boolean;
  exportPrevisitPatientTobaccoHistoryAlwaysEnabled: boolean;
  exportPrevisitPatientUploadsEnabled: boolean;
  exportPrevisitPatientUploadStatusesEnabled: boolean;
  exportPrevisitStatusToAppointmentNote: boolean;
  exportProblemGroupsAtCheckIn: boolean;
  exportProcessedRefundsJobEnabled: boolean;
  exportRegistrationNoteEnabled: boolean;
  exportResidualUpdatedPrevisitPatientInfoClinicalCommentEnabled: boolean;
  exportReviewRequiredToAppointmentNote: boolean;
  exportTerminatedInsurancesEnabled: boolean;
  exportUpdatedPrevisitFamilyMedicalHistoryEnabled: boolean;
  exportUpdatedPrevisitPatientAllergiesEnabled: boolean;
  exportUpdatedPrevisitPatientInfoGeneralCommentEnabled: boolean;
  exportUpdatedPrevisitPatientInformationEnabled: boolean;
  exportUpdatedPrevisitPatientMedicalHistoryEnabled: boolean;
  exportUpdatedSocialHistoryEnabled: boolean;
  exportUpdatedSurgicalHistoryEnabled: boolean;
  exportVerifiedRegistrationEnabled: boolean;
  exportVerifyRegistrationSections: VerifyRegistrationSections[];
  extractOnlyPrimaryCarePhysicians: boolean;
  extractOutstandingBalancesEnabled: ExtractOutstandingBalancesEnabled;
  extractPatientDocumentsKindsEnabled: PatientAdvisory.Recommendation[];
  extractPatientEmploymentSource: ExtractPatientEmploymentSource;
  extractPatientInsuranceRteInformationEnabled: boolean;
  extractPatientUploadDocumentTypes: DocumentType[];
  facilityNpi: string;
  familyMedicalHistoryRelationships: FamilyRelationship[];
  fhirFreezeAppointmentKinds: AppointmentKind[];
  forcePrevisitZipCodeAuth: boolean;
  forceTwoFactor: boolean;
  formScoreBasedMessageSettingsInGeneralComment: Partial<
    Record<FormScoreBasedMessageSettings, string>
  >;
  frontDesk2FAIdleTimeoutSeconds: number | null;
  frontDeskAuthInputIdleTimeoutSeconds: number | null;
  frontDeskCheckInEnabled: boolean;
  frontDeskErrorTimeoutSeconds: number | null;
  frontDeskKBAIdleTimeoutSeconds: number | null;
  frontDeskKbaRequireEncounterStartTime: boolean | null;
  frontDeskKbaRequirePhoneNumber: boolean | null;
  frontDeskKbaRequireProviderName: boolean | null;
  frontDeskKbaRequireZip: boolean | null;
  frontDeskQuestionnaireIdleTimeoutSeconds: number | null;
  frontdeskSuccessfulCompletionMessage: LocalizedPreference;
  frontDeskSuccessTimeoutSeconds: number | null;
  generalCommentSections: MultiOptionSections<GeneralCommentField>;
  generatedDocumentFormNameOverride: LocalizedPreference;
  hccSuspectingConditionsSupported: string[];
  hideEncounterStartTimeByVisitKind: string[];
  hl7CalendarSyncEnabled: boolean;
  ignoredGroupedAppointmentsStandardVisitReasons: string[];
  includeAppointmentInfoInGeneralComment: boolean;
  includeEmergencyContactInGeneralComment: boolean;
  includeGuarantorInGeneralComment: boolean;
  includeInsuranceInformationInGeneralComment: boolean;
  includeMedicarePatientEmploymentInGeneralComment: boolean;
  includeTerminatedInsuranceInGeneralComment: boolean;
  insurancePlansToExport: HealthPlansToExport[];
  ingestVisitContacts: boolean;
  payorsToSkipAutoDetermineCopay: string[];
  medicalConditionExcludedList: string[];
  medicalConditionList: string[];
  familyMedicalConditionList: string[];
  proceduresOptionsPreference: string[];
  medicareAdvantagePlanNames: string[];
  minimumDuplicateNotificationTime: TimeLookahead | null;
  minimumMessagingAge: number | null;
  mobileVisitReasons: string[];
  onlyShowExtractedGuarantors: boolean;
  patientAlcoholUsageFrequencies: Frequency[];
  patientAlcoholUsageQuantities: string[];
  patientAppRteEnabled: boolean;
  patientEditActivePhotoIdEnabled: boolean;
  patientEnvironmentLivesWithOptions: PatientEnvironment.LivesWith[];
  patientEnvironmentLivingSituationOptions: PatientEnvironment.LivingSituation[];
  patientEnvironmentParentsMaritalStatusOptions: PatientEnvironment.ParentsMaritalStatus[];
  patientEnvironmentRisksOptions: PatientEnvironment.Risk[];
  patientGuarantorDetailsEnabled: boolean;
  patientGuarantorFieldsToExclude: Guarantor.OptionalGuarantorFields[];
  patientInstructionsReviewedComment: LocalizedPreference;
  patientInsuranceInformationPayors: string[];
  patientNotificationPhoneNumberKindsAllowed: PhoneNumberKind[];
  patientPreviousBalancesIngestionPractice: string | null;
  patientRelationshipsToGuarantor: RelationshipPreference;
  patientRelationshipsToSubscriber: RelationshipPreference;
  patientSubstanceUsageFrequencies: Frequency[];
  paymentsV2: boolean;
  paymentConfirmationEmail: LocalizedEmail;
  paymentProcessedEmail: LocalizedEmail;
  photoIdCollectionFrequency: TimeLookahead | null;
  practiceCardOptions: PracticeCardOption[];
  preferredPhoneNumbersSms: string[];
  preferredPhoneNumbersVoice: string[];
  previousBalanceCollectionSetting: PreviousBalanceCollectionSetting;
  previsitApplicationPreloadCode: string;
  previsitAddressValidCharacterSet: PrevisitAddressValidCharacterSet;
  previsitAppointmentGroupingTimeframe: Timeframe | null;
  previsitConfirmCancelAfterLogin: boolean;
  previsitConfirmCancelFormSetRedirectEnabled: boolean;
  previsitConfirmCancelMinimumCancelTime: TimeLookahead | null;
  previsitDefaultAdditionalLoginInputsOptions: PrevisitLoginInput[];
  previsitDefaultCheckRequiredDocuments: boolean;
  previsitDefaultExportEmploymentStatusForMinors: EmploymentStatus | null;
  previsitDefaultExportMaritalStatusForMinors: MaritalStatus | null;
  previsitDefaultTwoFactorAuthEnabled: boolean;
  previsitEditGuarantorEnabled: boolean;
  previsitEditInsuranceEnabled: boolean;
  previsitEthnicityOptions: Previsit.Ethnicity[];
  previsitFirstEmail: LocalizedEmail;
  previsitFirstMessage: LocalizedPreference;
  previsitFormSetAuthBypassCodeEnabled: boolean;
  previsitFormSetExpireTimeAfterEncounter: TimeLookahead | null;
  previsitFormSetPatientLevelExpirationDays: number;
  previsitIntroPageMessageOutreach: LocalizedPreference;
  previsitIntroPageMessagePostVisit: LocalizedPreference;
  previsitIntroPageMessagePreVisit: LocalizedPreference;
  previsitLastPageMessageOutreach: LocalizedPreference;
  previsitLastPageMessagePostVisit: LocalizedPreference;
  previsitLastPageMessagePreVisit: LocalizedPreference;
  previsitMaritalStatusOptions: MaritalStatus[];
  previsitMedicationComplianceOptions: Medication.Status[];
  previsitMergeSubscriberGuarantorsEnabled: boolean;
  previsitMultipleLanguages: boolean;
  previsitMultipleRaces: boolean;
  previsitPayorDisplayMapping: Record<string, string>;
  previsitPreferredLanguageOptions: Language[];
  previsitPreferredPronounOptions: Patient.Pronoun[];
  previsitPrimitiveDynamicUpdate: boolean;
  previsitProviderNameDisplays: ProviderNameDisplayOption[];
  previsitReminderEmail: LocalizedEmail;
  previsitReminderMessage: LocalizedPreference;
  previsitSexOptions: Sex[];
  previsitSignInSupportPage: LocalizedPreference;
  previsitSkipIntroSingleForm: boolean;
  previsitSupportedLanguages: Language[];
  previsitUseNotionalSchedulingTimeSlots: boolean;
  previsitUsePhoneNumberAsDefault: PhoneNumberKind[];
  previsitWrittenLanguageOptions: Language[];
  primitivesToSyncForGroupedAppointments: PrimitivesToSyncForGroupedAppointments[];
  priorAuthorizationApprovedNote: string;
  priorAuthorizationRejectedNote: string;
  priorAuthorizationPendingNote: string;
  priorAuthorizationNotRequiredNote: string;
  problemGroupsExportEnabled: boolean;
  proceduresToExportAsOther: Procedure.Procedure[];
  providerNameFormat: { [location in NameLocation]?: string };
  provisionalPatientLastNameFormattingMode: ProvisionalPatientLastNameFormattingMode | null;
  raceOptions: Patient.Race[];
  raceOptionsDisplayOverrides: Partial<Record<Patient.Race, string>>;
  registrationDefaultAdditionalLoginInputs: PatientLoginInput[];
  registrationDefaultAdditionalLoginInputsRequiredMatchCount: number | null;
  registrationDefaultEnableContactVerification: boolean;
  registrationDefaultEnableFhirEhrReconciliation: boolean;
  registrationDefaultFhirEhrReconciliation: FhirReconciliationType;
  registrationMaxLoginAttempts: number | null;
  registrationEncounterEhrReferenceCreation: RegistrationEncounterEhrReferenceCreationType;
  religionOptions: Patient.Religion[];
  requireAccountNumberForPaymentBalances: boolean | null;
  requireKbaAuth: boolean;
  reschedulingAppointmentKindEnabledMapping: Record<AppointmentKind, AppointmentKind[]>;
  reschedulingPreserveAppointmentEnabled: boolean;
  resolvePaymentUsingExportResult: boolean;
  respectAllowableContactMethods: boolean;
  respectEhrPatientCommunicationConsents: boolean;
  retrySmsAsVoiceForFingerprintNotifications: boolean;
  reviewScreenCustomPatientGenderLabel: LocalizedPreference;
  rteUnsupportedPayors: string[];
  rteInsuranceCoverageMatchingPrimaryIdentifier: RteInsuranceCoverageMatchingPrimaryIdentifier | null;
  scheduleAppointmentRedirectUrl: string | null;
  schedulingCacheInvalidationTime: TimeLookahead | null;
  schedulingCacheLookaheadTime: TimeLookahead | null;
  schedulingCacheRefreshTime: TimeLookahead | null;
  schedulingCacheSlotTypes: AppointmentKind[];
  schedulingCronCacheSlotTypes: AppointmentKind[];
  schedulingDefaultEndLookaheadTime: TimeLookahead | null;
  schedulingDefaultStartLookaheadTime: TimeLookahead | null;
  schedulingEndpointsEnabled: boolean;
  schedulingFetchSlotBeforeBookingEnabled: boolean;
  schedulingRequireSlotsForForms: boolean;
  selfPayFlatFeeEnabled: boolean;
  sessionInactivityLimitSeconds: number;
  sessionMaxLengthSeconds: number;
  genderIdentityOptions: PatientSocialHistory.SexualIdentity[];
  genderIdentitiesToSkipExport: PatientSocialHistory.SexualIdentity[];
  showArrivalTimeOnly: boolean;
  singleOutstandingBalanceForEncounter: boolean;
  skipSendingAppointmentConfirmationOnBooking: boolean;
  smsEmailThenVoiceEnabled: boolean;
  softDeleteOldInsurancesWhenNoInsuranceExtracted: boolean;
  ssoClientId: string;
  ssoDeepLink: string;
  ssoIssUrl: string;
  ssoRedirectUri: string;
  statementDescriptorSuffix: string;
  stripeManualPayoutTime: string;
  stripeTestModeEnabled: boolean;
  subscriberRelationshipsToGuarantor: RelationshipPreference;
  substanceNameOptions: SubstanceUsage.SubstanceName[];
  substanceUsageStatuses: SubstanceUsage.Status[];
  tobaccoStatuses: PatientSocialHistory.TobaccoStatus[];
  tobaccoTreatments: PatientSocialHistory.TobaccoTreatment[];
  unsupportedCardBrands: Payment.CardBrand[];
  urgentCareCheckInConfirmationContentId: string | null;
  urgentCareCheckInConfirmationEmailContentId: string | null;
  urgentCareCheckInErrorContentId: string | null;
  urgentCareCheckInErrorEmailContentId: string | null;
  urgentCareConfirmationNotificationSendTimeMinuteOffset: number | null;
  useEncounterGuarantorAndInsuranceEnabled: boolean;
  useGenericPhoneAsCellPhoneFallback: boolean;
  virtualVisitReasons: string[];
  workflowsRequiredForAppointmentCheckIn: Workflow.Kind[];
}

export type NullablePreferences = {
  [P in keyof Preferences]: Preferences[P] | null;
};

export interface BasePreferenceReferenceKeys {
  practiceId: string | null;
  practiceGroupId: string | null;
  organizationId: string | null;
}

export type UpdateValues = Partial<NullablePreferences>;
export type CreateValues = NullablePartial<BasePreferenceReferenceKeys> & {
  preferences: UpdateValues;
};

export type PracticeIdPreference = NullablePreferences & PracticeEntity.PracticeEntity;
export type PracticeGroupIdPreference = NullablePreferences & PracticeEntity.PracticeGroupEntity;
export type OrganizationIdPreference = NullablePreferences & PracticeEntity.OrganizationEntity;
export type BasePracticePreference =
  | PracticeIdPreference
  | PracticeGroupIdPreference
  | OrganizationIdPreference;

export type PracticePreference = BaseItem & BasePracticePreference;

export interface BasePracticePreferenceHistory {
  practicePreferenceId: string;
  userId: string;
  preferences: PracticePreference;
}
export type PracticePreferenceHistory = BaseItem & BasePracticePreferenceHistory;

export type PreferenceOptions = {
  [K in keyof Preferences]: {
    displayName?: string;
    description?: string;
    options: Preferences[K][];
    default: Preferences[K];
    type: PreferenceType;
  };
};

export enum GroupName {
  AutomatedChartReview = 'Automated Chart Reviews',
  Documentation = 'Documentation',
  FrontDeskIpadApp = 'Front Desk iPad App',
  General = 'General',
  OutpatientCheckInAndReg = 'Outpatient Check-In & Registration',
  PatientInsurance = 'Patient Insurance',
  PatientNotifications = 'Patient Notifications',
  PatientOutreach = 'Patient Outreach',
  PaymentCollection = 'Payment Collection',
  ProviderMobileApp = 'Provider Mobile App',
  PriorAuthorization = 'Prior Authorization',
  Scheduling = 'Scheduling',
  DigitalCheckIn = 'Digital Check-In',
  UrgentCare = 'Urgent Care',
}

export enum SubGroupName {
  AllowableFieldValues = 'Allowable Field Values',
  Guarantor = 'Guarantor',
  IngestExportSettings = 'Ingest / Export Settings',
  InsuranceAutomation = 'Insurance Automation',
  RpaSettings = 'RPA Settings',
  RTE = 'RTE',
  UserExperience = 'User Experience',
}

export interface SubGroup {
  subGroupName: SubGroupName | null;
  preferenceNames: (keyof Preferences)[];
}

export interface PreferenceGroup {
  groupName: GroupName;
  subGroups: SubGroup[];
}
